import { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  Polyline,
  type Libraries,
} from '@react-google-maps/api'
import { Card } from '@/shared/ui/Card'
import { Avatar } from '@/shared/ui/Avatar'
import { Spinner } from '@/shared/ui/Spinner'
import { Plus, Minus, Locate, Users, GraduationCap, Search, X, Ruler } from 'lucide-react'
import { cn, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import { GOOGLE_MAPS_API_KEY } from '@/shared/constants'
import { useNearestTrainers, useStudentsUnderRadius, useSchoolTrainersForMap } from '@/api/hooks'
import { useAuthStore } from '@/store/useAuthStore'

// Default view: the whole UAE. Module-level constants so their identity is stable —
// passing a fresh object literal here makes GoogleMap re-centre on every render,
// which fights the user's panning.
const DEFAULT_CENTER = { lat: 24.2765, lng: 54.346 } as const
const DEFAULT_ZOOM = 7
const MAX_CLUSTER_ZOOM = 17
const CONTAINER_STYLE = { width: '100%', height: '560px' }
const LIBRARIES: Libraries = ['places']

// Clean, low-clutter basemap so the data markers are the loudest thing on screen.
const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  gestureHandling: 'greedy',
  clickableIcons: false,
  minZoom: 5,
  maxZoom: 20,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', stylers: [{ color: '#cfe4f2' }] },
    { featureType: 'landscape', stylers: [{ color: '#f6f7f8' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d8dde3' }] },
  ],
}

interface Person {
  id: string
  location: { lat: number; lng: number }
  photoUrl?: string
  name?: string
  phone?: string
  email?: string
}

type Kind = 'trainers' | 'students'

const KIND_COLOR: Record<Kind, { dot: string; ring: string; bubble: string }> = {
  trainers: { dot: '#2563eb', ring: 'ring-blue-500',  bubble: 'bg-blue-600' },
  students: { dot: '#f68b2c', ring: 'ring-brand-500', bubble: 'bg-brand-500' },
}

// These endpoints wrap their rows differently — sometimes `data` is the array,
// sometimes it's an object holding it (`data.data`, `data.rows`, …). Dig out the
// first array we find so a shape change can't crash the map.
function toRows(raw: any): any[] {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  for (const key of ['data', 'rows', 'list', 'items', 'result', 'trainers', 'students', 'users']) {
    const v = raw[key]
    if (Array.isArray(v)) return v
    if (v && typeof v === 'object') {
      const nested = toRows(v)
      if (nested.length) return nested
    }
  }
  return []
}

// Normalise the two different API response shapes into a common Person[].
function mapTrainers(raw: any): Person[] {
  const rows = toRows(raw)
  return rows.map((t: any) => ({
    id: String(t.id),
    name: t.name,
    photoUrl: t.photo_url,
    phone: t.phone_number ?? t.phone,
    email: t.email,
    location: t.address
      ? { lat: Number(t.address.latitude) || 0, lng: Number(t.address.longitude) || 0 }
      : { lat: 0, lng: 0 },
  }))
}
// get-school-trainer-list rows wrap the trainer in `user`, and the address can
// arrive as `address` (single) or `user_addresses`/`addresses` (list).
function mapSchoolTrainers(raw: any): Person[] {
  const rows = toRows(raw)
  return rows.map((row: any) => {
    const u = row?.user ?? row
    const addrs = u?.user_addresses ?? u?.addresses ?? row?.user_addresses ?? row?.addresses
    const list: any[] = Array.isArray(addrs) ? addrs : []
    const a = list.find((x: any) => x?.is_default) ?? list[0] ?? u?.address ?? row?.address
    return {
      id: String(u?.id ?? row?.user_id ?? row?.id),
      name: u?.name ?? row?.name,
      photoUrl: u?.photo_url ?? row?.photo_url,
      phone: u?.phone_number ?? u?.phone,
      email: u?.email,
      location: a
        ? { lat: Number(a.latitude) || 0, lng: Number(a.longitude) || 0 }
        : { lat: 0, lng: 0 },
    }
  })
}
function mapStudents(raw: any): Person[] {
  const rows = toRows(raw)
  return rows.map((s: any) => ({
    id: String(s.id),
    name: s.name,
    photoUrl: s.photo_url,
    phone: s.phone_number ?? s.phone,
    email: s.email,
    location: s.addresses?.length
      ? { lat: Number(s.addresses[0].latitude) || 0, lng: Number(s.addresses[0].longitude) || 0 }
      : { lat: 0, lng: 0 },
  }))
}

// ── Clustering ────────────────────────────────────────────────────────────────
// Grid clustering in lat/lng space. The cell shrinks as zoom grows, so wide views
// aggregate into few bubbles and close views break apart into individual pins —
// the Bayut behaviour.
interface Cluster {
  key: string
  lat: number
  lng: number
  items: Person[]
}

function buildClusters(people: Person[], zoom: number, bounds: google.maps.LatLngBoundsLiteral | null): Cluster[] {
  // Degrees per grid cell. 360° / 2^zoom is one map tile; /3 gives ~3 clusters per
  // tile width, which reads well at every zoom.
  const cell = 360 / Math.pow(2, zoom) / 3
  const cells = new Map<string, Person[]>()

  for (const p of people) {
    const { lat, lng } = p.location
    if (!lat || !lng) continue
    // Only cluster what's actually on screen (padded), like Bayut does.
    if (bounds) {
      const padLat = (bounds.north - bounds.south) * 0.15
      const padLng = (bounds.east - bounds.west) * 0.15
      if (lat < bounds.south - padLat || lat > bounds.north + padLat) continue
      if (lng < bounds.west - padLng || lng > bounds.east + padLng) continue
    }
    const key = `${Math.floor(lat / cell)}:${Math.floor(lng / cell)}`
    const list = cells.get(key)
    if (list) list.push(p)
    else cells.set(key, [p])
  }

  const out: Cluster[] = []
  for (const [key, items] of cells) {
    // Centroid, so the bubble sits over the actual mass of points.
    const lat = items.reduce((s, p) => s + p.location.lat, 0) / items.length
    const lng = items.reduce((s, p) => s + p.location.lng, 0) / items.length
    out.push({ key, lat, lng, items })
  }
  return out
}

// Bubble grows with the count, capped so it never dominates the map.
function bubbleSize(n: number) {
  if (n < 10) return 40
  if (n < 50) return 48
  if (n < 200) return 56
  return 64
}

const centerOffset = (w: number, h: number) => ({ x: -(w / 2), y: -(h / 2) })
const cardOffset   = (w: number, h: number) => ({ x: -(w / 2), y: -(h + 16) })

// Great-circle distance between two lat/lng points, in kilometres.
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371
  const dLat = (b.lat - a.lat) * Math.PI / 180
  const dLng = (b.lng - a.lng) * Math.PI / 180
  const la1 = a.lat * Math.PI / 180
  const la2 = b.lat * Math.PI / 180
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

// ── Spiderfy ──────────────────────────────────────────────────────────────────
// People stacked on identical coordinates can never be separated by zooming, so
// we fan them out on legs around their shared point instead (the classic
// "spiderfy" from Leaflet.markercluster / OverlappingMarkerSpiderfier).
// Offsets are in PIXELS, laid out inside a single OverlayView anchored at the
// cluster centroid — no lat/lng projection maths needed.
function spiderPositions(n: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = []
  if (n <= 9) {
    // Small groups: an even circle.
    const sep = 32
    const r = Math.max(38, (sep * n) / (2 * Math.PI))
    for (let i = 0; i < n; i++) {
      const a = (2 * Math.PI * i) / n - Math.PI / 2
      out.push({ x: r * Math.cos(a), y: r * Math.sin(a) })
    }
  } else {
    // Larger groups: an Archimedean spiral so legs don't overlap.
    const sep = 28, lenStart = 18, lenFactor = 4.5
    let angle = 0
    for (let i = 0; i < n; i++) {
      const len = lenStart + lenFactor * angle
      out.push({ x: len * Math.cos(angle), y: len * Math.sin(angle) })
      angle += sep / len + i * 0.0005
    }
  }
  return out
}

// Are these points close enough that zooming will never pull them apart?
// ~1e-4 deg ≈ 11 m — below that, pins overlap even at max zoom.
function isCoincident(items: Person[]): boolean {
  if (items.length < 2) return true
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity
  for (const p of items) {
    minLat = Math.min(minLat, p.location.lat); maxLat = Math.max(maxLat, p.location.lat)
    minLng = Math.min(minLng, p.location.lng); maxLng = Math.max(maxLng, p.location.lng)
  }
  return (maxLat - minLat) < 1e-4 && (maxLng - minLng) < 1e-4
}

// ── Markers ───────────────────────────────────────────────────────────────────
function ClusterBubble({ cluster, kind, onClick }: { cluster: Cluster; kind: Kind; onClick: () => void }) {
  const n = cluster.items.length
  const size = bubbleSize(n)
  return (
    <OverlayView
      position={{ lat: cluster.lat, lng: cluster.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={centerOffset}
    >
      <button
        onClick={onClick}
        style={{ width: size, height: size }}
        aria-label={`${n} ${kind} — zoom in`}
        className={cn(
          'relative flex items-center justify-center rounded-full text-white font-black shadow-lg',
          'ring-4 ring-white/60 hover:scale-110 hover:ring-white transition-transform duration-150 cursor-pointer',
          KIND_COLOR[kind].bubble
        )}
      >
        <span className={cn('leading-none', n > 99 ? 'text-xs' : 'text-sm')}>{n > 999 ? '999+' : n}</span>
      </button>
    </OverlayView>
  )
}

function PersonPin({ person, kind, onHover }: { person: Person; kind: Kind; onHover: (id: string | null) => void }) {
  const c = KIND_COLOR[kind]
  return (
    <OverlayView
      position={person.location}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={centerOffset}
    >
      <div
        onMouseEnter={() => onHover(person.id)}
        onMouseLeave={() => onHover(null)}
        className="relative cursor-pointer group"
        style={{ width: 34, height: 34 }}
      >
        <div className={cn(
          'w-[34px] h-[34px] rounded-full bg-white shadow-md ring-2 overflow-hidden',
          'group-hover:scale-110 transition-transform duration-150', c.ring
        )}>
          <img src={person.photoUrl || DUMMY_AVATAR} alt={person.name ?? ''} onError={onAvatarError} className="w-full h-full object-cover"/>
        </div>
        {/* pointer */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0"
          style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: `6px solid ${c.dot}` }}
        />
      </div>
    </OverlayView>
  )
}

// An expanded cluster: every member fanned out on its own leg, individually
// hoverable. Rendered as one OverlayView anchored at the centroid, with children
// placed at pixel offsets.
function SpiderCluster({ cluster, kind, onCollapse }: { cluster: Cluster; kind: Kind; onCollapse: () => void }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const c = KIND_COLOR[kind]
  const items = cluster.items
  const pos = useMemo(() => spiderPositions(items.length), [items.length])

  // Container must cover the widest leg; the hover card may overflow it, which is fine.
  const maxR = pos.reduce((m, p) => Math.max(m, Math.hypot(p.x, p.y)), 0)
  const size = Math.ceil(2 * (maxR + 24))
  const mid = size / 2
  const hovered = hoverIdx != null ? items[hoverIdx] : null

  return (
    <OverlayView
      position={{ lat: cluster.lat, lng: cluster.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={centerOffset}
    >
      <div className="relative" style={{ width: size, height: size }}>
        {/* Legs */}
        <svg width={size} height={size} className="absolute inset-0 pointer-events-none">
          {pos.map((p, i) => (
            <line key={i} x1={mid} y1={mid} x2={mid + p.x} y2={mid + p.y}
              stroke={c.dot} strokeWidth={1.5} strokeOpacity={0.45}/>
          ))}
        </svg>

        {/* Collapse hub */}
        <button
          onClick={onCollapse}
          aria-label="Collapse group"
          className="absolute w-5 h-5 rounded-full bg-white shadow ring-1 ring-black/10 flex items-center justify-center hover:bg-gray-50 transition-colors"
          style={{ left: mid - 10, top: mid - 10 }}
        >
          <span className="w-2 h-2 rounded-full" style={{ background: c.dot }}/>
        </button>

        {/* Fanned members */}
        {items.map((p, i) => (
          <div
            key={p.id}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
            className="absolute cursor-pointer"
            style={{ left: mid + pos[i].x - 17, top: mid + pos[i].y - 17, width: 34, height: 34, zIndex: hoverIdx === i ? 3 : 2 }}
          >
            <div className={cn(
              'w-[34px] h-[34px] rounded-full bg-white shadow-md ring-2 overflow-hidden transition-transform duration-150',
              hoverIdx === i && 'scale-110', c.ring
            )}>
              <img src={p.photoUrl || DUMMY_AVATAR} alt={p.name ?? ''} onError={onAvatarError} className="w-full h-full object-cover"/>
            </div>
          </div>
        ))}

        {/* Hover card, placed beside its own pin rather than the shared centroid */}
        {hovered && hoverIdx != null && (
          <div
            className="absolute flex flex-col items-center gap-1 rounded-xl bg-white px-3 py-2.5 shadow-xl ring-1 ring-black/5 text-center pointer-events-none"
            style={{ width: 172, left: mid + pos[hoverIdx].x - 86, top: mid + pos[hoverIdx].y - 17 - 96, zIndex: 4 }}
          >
            {hovered.photoUrl
              ? <img src={hovered.photoUrl} alt={hovered.name} className="w-11 h-11 rounded-full object-cover"/>
              : <Avatar name={hovered.name ?? 'User'} size="lg"/>}
            <h4 className="m-0 text-sm font-bold text-gray-900 leading-tight truncate w-full">{hovered.name ?? 'User'}</h4>
            {hovered.phone && <p className="m-0 text-xs text-gray-500 leading-tight">{hovered.phone}</p>}
            {hovered.email && <p className="m-0 text-xs text-gray-400 leading-tight truncate w-full">{hovered.email}</p>}
          </div>
        )}
      </div>
    </OverlayView>
  )
}

// A clear, unmistakable highlight for a searched-and-selected person — a
// pulsing ring around their pin plus a drop-pin pointer beneath it, so they
// stand out from every other pin on the map at a glance.
function SelectedMarker({ person, kind }: { person: Person; kind: Kind }) {
  const c = KIND_COLOR[kind]
  return (
    <OverlayView
      position={person.location}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={centerOffset}
    >
      <div className="relative pointer-events-none" style={{ width: 34, height: 34 }}>
        <span className="absolute inset-0 -m-3.5 rounded-full animate-ping" style={{ background: c.dot, opacity: 0.4 }}/>
        <span className="absolute inset-0 -m-2 rounded-full ring-4" style={{ boxShadow: `0 0 0 3px ${c.dot}` }}/>
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-3.5 w-0 h-0"
          style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `9px solid ${c.dot}` }}
        />
      </div>
    </OverlayView>
  )
}

function DetailCard({ person }: { person: Person }) {
  return (
    <OverlayView
      position={person.location}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={cardOffset}
    >
      <div className="flex flex-col items-center gap-1 rounded-xl bg-white px-3 py-2.5 shadow-xl ring-1 ring-black/5 text-center pointer-events-none" style={{ width: 172 }}>
        {person.photoUrl
          ? <img src={person.photoUrl} alt={person.name} className="w-11 h-11 rounded-full object-cover"/>
          : <Avatar name={person.name ?? 'User'} size="lg"/>}
        <h4 className="m-0 text-sm font-bold text-gray-900 leading-tight truncate w-full">{person.name ?? 'User'}</h4>
        {person.phone && <p className="m-0 text-xs text-gray-500 leading-tight">{person.phone}</p>}
        {person.email && <p className="m-0 text-xs text-gray-400 leading-tight truncate w-full">{person.email}</p>}
      </div>
    </OverlayView>
  )
}

// Type-to-search picker for the distance panel — same search-then-pick pattern
// as the main header search, but scoped to one list and rendering its pick as
// a small chip inside the input instead of jumping the map.
function PersonSearchSelect({ people, selectedId, onSelect, placeholder }: {
  people: Person[]; selectedId: string; onSelect: (id: string) => void; placeholder: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const selected = people.find(p => p.id === selectedId) ?? null

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    const pool = q
      ? people.filter(p =>
          p.name?.toLowerCase().includes(q) ||
          p.phone?.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q))
      : people
    return pool.slice(0, 8)
  }, [people, query])

  return (
    <div ref={boxRef} className="relative min-w-[180px]">
      <input
        type="text"
        value={open ? query : (selected?.name ?? '')}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => { setQuery(''); setOpen(true) }}
        placeholder={placeholder}
        className="w-full text-xs font-medium border border-gray-200 rounded-lg ps-2.5 pe-7 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-100 truncate"
      />
      {selected && (
        <button
          onClick={() => { onSelect(''); setQuery(''); setOpen(false) }}
          aria-label="Clear"
          className="absolute end-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <X size={12} strokeWidth={2.5}/>
        </button>
      )}
      {open && (
        <div className="absolute top-full mt-1 w-56 max-h-56 overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-black/5 z-20">
          {results.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-gray-400 text-center">No matches</div>
          ) : results.map(p => (
            <button
              key={p.id}
              onClick={() => { onSelect(p.id); setQuery(''); setOpen(false) }}
              className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-gray-50 transition-colors"
            >
              <img src={p.photoUrl || DUMMY_AVATAR} alt="" onError={onAvatarError} className="w-6 h-6 rounded-full object-cover shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-gray-900 truncate">{p.name ?? 'Unnamed'}</div>
                {p.phone && <div className="text-[10px] text-gray-400 truncate">{p.phone}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Straight line between a chosen trainer and a chosen student, with a floating
// "X.X km" chip at the midpoint — the actual "distance between that and this".
function DistanceLine({ from, to }: { from: Person; to: Person }) {
  const km = haversineKm(from.location, to.location)
  const mid = { lat: (from.location.lat + to.location.lat) / 2, lng: (from.location.lng + to.location.lng) / 2 }
  return (
    <>
      <Polyline
        path={[from.location, to.location]}
        options={{ strokeColor: '#1C252E', strokeOpacity: 0.7, strokeWeight: 2.5, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 }, offset: '0', repeat: '14px' }], clickable: false }}
      />
      {/* Centered via CSS transform, not getPixelPositionOffset's measured-width
          callback — that measurement is racy for a whitespace-nowrap label with
          variable text length and can leave the chip mispositioned/invisible.
          Positioning (the translate) and pill styling are split into two nested
          divs: OverlayView's own wrapper can force a width on our top-level div,
          which fights plain block sizing — inline-block on the inner pill makes
          it size strictly to its own text regardless of what the outer div does. */}
      <OverlayView position={mid} mapPaneName={OverlayView.FLOAT_PANE} getPixelPositionOffset={() => ({ x: 0, y: 0 })}>
        <div className="pointer-events-none -translate-x-1/2 -translate-y-1/2">
          <div className="inline-block whitespace-nowrap rounded-full bg-gray-900 text-white text-xs font-bold px-2.5 py-1 shadow-lg z-50">
            {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`} apart
          </div>
        </div>
      </OverlayView>
    </>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
// Every mount of <GoogleMap> is a billable Maps JS API load, and this card
// sits on the admin dashboard — the single most-visited page. Loading it
// unconditionally would bill for every dashboard visit, even the many where
// nobody scrolls down this far. So HeatMap itself only reserves the layout
// space and watches for the card entering the viewport; the real map (data
// fetch + GoogleMap mount) lives in HeatMapLoaded, mounted once and never
// unmounted again for the rest of the session.
export function HeatMap() {
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (hasBeenVisible) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some(e => e.isIntersecting)) setHasBeenVisible(true) },
      // Starts loading a little before it's actually on screen, so by the time
      // the user finishes scrolling to it the map already feels ready.
      { rootMargin: '400px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasBeenVisible])

  return (
    <div ref={containerRef}>
      {hasBeenVisible ? <HeatMapLoaded/> : (
        <Card>
          <div className="mb-4">
            <div className="font-bold text-gray-900">Users Map</div>
            <div className="text-xs text-gray-400 mt-0.5">Loads automatically as you scroll to it</div>
          </div>
          <div className="h-[560px] flex items-center justify-center bg-gray-50 rounded-xl">
            <Spinner/>
          </div>
        </Card>
      )}
    </div>
  )
}

function HeatMapLoaded() {
  // School admins see the trainer layer only — no student data on their map.
  const isSchoolAdmin = useAuthStore(s => s.user)?.user_type === 'school_admin'
  const [selected, setSelected] = useState<Kind>('trainers')
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  // Separate from hoveredId (which also fires on plain mouse hover) so a
  // search selection keeps its clear pointer even after the mouse moves away.
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  const [spiderKey, setSpiderKey] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  // Distance check: pick one trainer + one student, see the line and km between them.
  const [distancePanelOpen, setDistancePanelOpen] = useState(false)
  const [distanceTrainerId, setDistanceTrainerId] = useState('')
  const [distanceStudentId, setDistanceStudentId] = useState('')
  const mapRef = useRef<google.maps.Map | null>(null)
  const searchBoxRef = useRef<HTMLDivElement | null>(null)

  const { isLoaded } = useJsApiLoader({
    id: 'drivys-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  })

  const { data: trainersRaw, isLoading: adminTrainersLoading } = useNearestTrainers(DEFAULT_CENTER, 500, !isSchoolAdmin)
  const { data: schoolTrainersRaw, isLoading: schoolTrainersLoading } = useSchoolTrainersForMap(isSchoolAdmin)
  const { data: studentsRaw, isLoading: studentsLoading } = useStudentsUnderRadius(DEFAULT_CENTER, 500, !isSchoolAdmin)

  const trainersLoading = isSchoolAdmin ? schoolTrainersLoading : adminTrainersLoading
  const trainers = useMemo(
    () => isSchoolAdmin ? mapSchoolTrainers(schoolTrainersRaw) : mapTrainers(trainersRaw),
    [isSchoolAdmin, schoolTrainersRaw, trainersRaw],
  )
  const students = useMemo(() => mapStudents(studentsRaw), [studentsRaw])
  // Only people with a real location make sense to pick for a distance check.
  const trainersWithLocation = useMemo(() => trainers.filter(p => p.location.lat && p.location.lng), [trainers])
  const studentsWithLocation = useMemo(() => students.filter(p => p.location.lat && p.location.lng), [students])
  const distanceTrainer = distanceTrainerId ? trainersWithLocation.find(p => p.id === distanceTrainerId) ?? null : null
  const distanceStudent = distanceStudentId ? studentsWithLocation.find(p => p.id === distanceStudentId) ?? null : null

  const people   = selected === 'trainers' || isSchoolAdmin ? trainers : students
  const plotted  = useMemo(() => people.filter(p => p.location.lat && p.location.lng), [people])
  const clusters = useMemo(() => buildClusters(plotted, zoom, bounds), [plotted, zoom, bounds])

  // Search across the currently selected group (name / phone / email).
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return []
    return people
      .filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.phone?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [people, searchQuery])

  // Close the results dropdown on outside click.
  useEffect(() => {
    if (!showResults) return
    const onClickOutside = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [showResults])

  // Once both a trainer and a student are picked, frame the map so both pins
  // (and the line between them) are actually visible without manual panning.
  useEffect(() => {
    if (!distanceTrainer || !distanceStudent) return
    const m = mapRef.current
    if (!m) return
    const b = new google.maps.LatLngBounds()
    b.extend(distanceTrainer.location)
    b.extend(distanceStudent.location)
    m.fitBounds(b, 100)
  }, [distanceTrainer, distanceStudent])

  // Keep zoom/bounds in sync once the map settles.
  const syncView = useCallback(() => {
    const m = mapRef.current
    if (!m) return
    const z = m.getZoom()
    // Zoom re-buckets every cluster, so any expanded fan is no longer meaningful.
    if (z != null) {
      setZoom(prev => { if (prev !== z) setSpiderKey(null); return z })
    }
    const b = m.getBounds()
    if (b) {
      const ne = b.getNorthEast()
      const sw = b.getSouthWest()
      setBounds({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() })
    }
  }, [])

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
    map.setCenter(DEFAULT_CENTER)
    map.setZoom(DEFAULT_ZOOM)
  }, [])

  // Click a cluster → drill in like Bayut. But zooming can only separate points
  // that actually differ; when they're stacked on one spot (or we're already at
  // max zoom) we fan them out instead, so every person stays individually visible.
  const expandCluster = useCallback((c: Cluster) => {
    const m = mapRef.current
    if (!m) return
    const z = m.getZoom() ?? DEFAULT_ZOOM
    if (isCoincident(c.items) || z >= MAX_CLUSTER_ZOOM) {
      setSpiderKey(c.key)
      m.panTo({ lat: c.lat, lng: c.lng })
      return
    }
    const b = new google.maps.LatLngBounds()
    c.items.forEach(p => b.extend(p.location))
    m.fitBounds(b, 80)
  }, [])

  // Jump the map to a searched person: pan/zoom to their location, drop any
  // active spiderfy, and drop a clear pointer on their pin that sticks around
  // even after the mouse moves away. The info card stays hover-only — it would
  // otherwise sit right on top of the pin and hide exactly where it's pointing.
  const selectPerson = useCallback((person: Person) => {
    setSearchQuery('')
    setShowResults(false)
    setSpiderKey(null)
    if (!person.location.lat && !person.location.lng) {
      setSelectedPersonId(null)
      return
    }
    const m = mapRef.current
    if (m) {
      m.panTo(person.location)
      m.setZoom(MAX_CLUSTER_ZOOM)
    }
    setSelectedPersonId(person.id)
  }, [])

  const nudgeZoom = (delta: number) => {
    const m = mapRef.current
    if (!m) return
    m.setZoom(Math.max(5, Math.min(20, (m.getZoom() ?? DEFAULT_ZOOM) + delta)))
  }
  const resetView = () => {
    const m = mapRef.current
    if (!m) return
    m.panTo(DEFAULT_CENTER)
    m.setZoom(DEFAULT_ZOOM)
  }

  const hovered = hoveredId ? plotted.find(p => p.id === hoveredId) ?? null : null
  const selectedPerson = selectedPersonId ? plotted.find(p => p.id === selectedPersonId) ?? null : null
  // A disabled query stays "pending" forever, so don't let the student layer
  // block the map when it's switched off for school admins.
  const loadingData = trainersLoading || (!isSchoolAdmin && studentsLoading)

  const Header = (
    <div className="mb-4 space-y-3">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <div className="font-bold text-gray-900">Users Map</div>
        <div className="text-xs text-gray-400 mt-0.5">
          {plotted.length} {selected} mapped
          {people.length !== plotted.length && ` · ${people.length - plotted.length} without location`}
        </div>
      </div>

      {/* Search */}
      <div ref={searchBoxRef} className="relative flex-1 min-w-[200px] max-w-xs">
        <Search size={14} strokeWidth={2.5} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input
          type="text"
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setShowResults(true) }}
          onFocus={() => setShowResults(true)}
          placeholder={`Search ${selected}…`}
          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 ps-8 pe-8 text-xs font-medium text-gray-700 placeholder:text-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-100 transition-colors"
        />
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(''); setShowResults(false) }}
            aria-label="Clear search"
            className="absolute end-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} strokeWidth={2.5}/>
          </button>
        )}

        {showResults && searchQuery.trim() && (
          <div className="absolute top-full mt-1.5 w-full max-h-72 overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-black/5 z-10">
            {searchResults.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">No {selected} found</div>
            ) : (
              searchResults.map(p => {
                const hasLocation = !!(p.location.lat && p.location.lng)
                return (
                  <button
                    key={p.id}
                    onClick={() => selectPerson(p)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-start hover:bg-gray-50 transition-colors"
                  >
                    <img src={p.photoUrl || DUMMY_AVATAR} alt={p.name ?? ''} onError={onAvatarError} className="w-7 h-7 rounded-full object-cover shrink-0"/>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-gray-900 truncate">{p.name ?? 'Unnamed'}</div>
                      {p.phone && <div className="text-[11px] text-gray-400 truncate">{p.phone}</div>}
                    </div>
                    {!hasLocation && (
                      <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400">no location</span>
                    )}
                  </button>
                )
              })
            )}
          </div>
        )}
      </div>

      {/* Segmented toggle — school admins only ever see the trainer layer */}
      <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1">
        {(([
          { key: 'trainers', icon: Users,         label: 'Trainers', count: trainers.length },
          ...(isSchoolAdmin ? [] : [{ key: 'students', icon: GraduationCap, label: 'Students', count: students.length }]),
        ]) as { key: Kind; icon: typeof Users; label: string; count: number }[]).map(({ key, icon: Icon, label, count }) => (
          <button
            key={key}
            onClick={() => { setSelected(key); setHoveredId(null); setSelectedPersonId(null); setSpiderKey(null); setSearchQuery(''); setShowResults(false) }}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors',
              selected === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Icon size={13} strokeWidth={2.5} className={selected === key ? (key === 'trainers' ? 'text-blue-600' : 'text-brand-500') : ''}/>
            {label}
            <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] leading-none',
              selected === key ? 'bg-gray-100 text-gray-600' : 'bg-gray-200/70 text-gray-500')}>{count}</span>
          </button>
        ))}
      </div>

      {/* Distance check toggle — trainer↔student, so admin only */}
      {!isSchoolAdmin && <button
        onClick={() => setDistancePanelOpen(o => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors border',
          distancePanelOpen ? 'bg-brand-50 text-brand-600 border-brand-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
        )}
      >
        <Ruler size={14} strokeWidth={2.5}/>
        Distance
      </button>}
    </div>

    {/* Distance picker row — choose one trainer + one student to see the line and km between them */}
    {distancePanelOpen && (
      <div className="flex items-center gap-2 flex-wrap rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5">
        <PersonSearchSelect
          people={trainersWithLocation}
          selectedId={distanceTrainerId}
          onSelect={setDistanceTrainerId}
          placeholder="Search trainer…"
        />
        <span className="text-gray-300 text-xs">→</span>
        <PersonSearchSelect
          people={studentsWithLocation}
          selectedId={distanceStudentId}
          onSelect={setDistanceStudentId}
          placeholder="Search student…"
        />

        {distanceTrainer && distanceStudent && (
          <span className="text-xs font-bold text-gray-700">
            {(() => {
              const km = haversineKm(distanceTrainer.location, distanceStudent.location)
              return km < 1 ? `${Math.round(km * 1000)} m apart` : `${km.toFixed(1)} km apart`
            })()}
          </span>
        )}

        {(distanceTrainerId || distanceStudentId) && (
          <button
            onClick={() => { setDistanceTrainerId(''); setDistanceStudentId('') }}
            className="text-xs font-semibold text-gray-400 hover:text-gray-600 ms-auto"
          >
            Clear
          </button>
        )}
      </div>
    )}
    </div>
  )

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Card>
        {Header}
        <div className="h-[560px] flex flex-col items-center justify-center text-center bg-gray-50 rounded-xl gap-1">
          <p className="text-sm font-medium text-gray-600">Google Maps API key not configured</p>
          <p className="text-xs text-gray-400">Set VITE_APP_GOOGLE_API_KEY in your environment to enable the heat map.</p>
        </div>
      </Card>
    )
  }

  if (!isLoaded) {
    return (
      <Card>
        {Header}
        <div className="h-[560px] flex items-center justify-center bg-gray-50 rounded-xl"><Spinner/></div>
      </Card>
    )
  }

  return (
    <Card>
      {Header}
      <div className="relative overflow-hidden rounded-xl ring-1 ring-gray-200">
        <GoogleMap
          mapContainerStyle={CONTAINER_STYLE}
          options={MAP_OPTIONS}
          onLoad={onLoad}
          onIdle={syncView}
          onClick={() => setSelectedPersonId(null)}
          onUnmount={() => { mapRef.current = null }}
        >
          {clusters.map(c => {
            if (c.items.length === 1)
              return <PersonPin key={c.items[0].id} person={c.items[0]} kind={selected} onHover={setHoveredId}/>
            if (c.key === spiderKey)
              return <SpiderCluster key={c.key} cluster={c} kind={selected} onCollapse={() => setSpiderKey(null)}/>
            return <ClusterBubble key={c.key} cluster={c} kind={selected} onClick={() => expandCluster(c)}/>
          })}
          {/* Selection only drops the clear pointer, not the info card — the card
              would sit right on top of the pin and hide exactly where it's pointing. */}
          {selectedPerson && !spiderKey && <SelectedMarker person={selectedPerson} kind={selected}/>}
          {hovered && !spiderKey && <DetailCard person={hovered}/>}

          {/* Distance check — shows both picks (regardless of the Trainers/Students
              toggle above) plus the line and distance between them. */}
          {distanceTrainer && distanceStudent && <DistanceLine from={distanceTrainer} to={distanceStudent}/>}
          {distanceTrainer && <SelectedMarker person={distanceTrainer} kind="trainers"/>}
          {distanceStudent && <SelectedMarker person={distanceStudent} kind="students"/>}
        </GoogleMap>

        {/* Zoom / reset controls */}
        <div className="absolute end-3 top-3 flex flex-col gap-1.5">
          <div className="flex flex-col overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
            <button onClick={() => nudgeZoom(1)} aria-label="Zoom in"
              className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors">
              <Plus size={16} strokeWidth={2.5}/>
            </button>
            <div className="h-px bg-gray-100"/>
            <button onClick={() => nudgeZoom(-1)} aria-label="Zoom out"
              className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors">
              <Minus size={16} strokeWidth={2.5}/>
            </button>
          </div>
          <button onClick={resetView} aria-label="Reset view"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white text-gray-600 shadow-lg ring-1 ring-black/5 hover:bg-gray-50 transition-colors">
            <Locate size={15} strokeWidth={2.5}/>
          </button>
        </div>

        {/* Legend */}
        <div className="absolute start-3 bottom-3 flex items-center gap-3 rounded-xl bg-white/95 px-3 py-2 shadow-lg ring-1 ring-black/5 backdrop-blur">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: KIND_COLOR[selected].dot }}/>
            Individual
          </span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
            <span className={cn('w-4 h-4 rounded-full ring-2 ring-white/70', KIND_COLOR[selected].bubble)}/>
            {spiderKey ? 'Expanded — click the hub to collapse' : 'Grouped — click to zoom / fan out'}
          </span>
        </div>

        {loadingData && (
          <div className="absolute start-3 top-3 flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-gray-500 shadow-lg ring-1 ring-black/5">
            <Spinner/> loading…
          </div>
        )}

        {!loadingData && plotted.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="rounded-xl bg-white/95 px-4 py-3 text-center shadow-lg ring-1 ring-black/5">
              <p className="text-sm font-semibold text-gray-600">No {selected} with a location yet</p>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export default HeatMap
